// src/pages/prodactions/production-summary/ProductionBarChart.jsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    Stack,
    useTheme
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import { database } from "../../../firebase";
import { ref, get } from "firebase/database";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../utils/formatLastUpdated";

// Fungsi untuk format ribuan tanpa desimal
const formatThousandsNoDecimal = (value) => {
    if (!value && value !== 0) return "-";
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "-";
    const roundedValue = Math.round(numValue);
    return roundedValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// Komponen Bar Chart untuk Production Summary
export const ProductionBarChart = ({ 
    productionSummary, 
    activeMonths, 
    selectedYear,
    totals 
}) => {
    const theme = useTheme();
    const [lastUpdated, setLastUpdated] = useState(null);

    // Fungsi untuk mendapatkan last_imported_at terbaru dari tb_daily_production
    useEffect(() => {
        const fetchLastUpdated = async () => {
            try {
                console.log("📥 Fetching last_imported_at from Firebase...");
                
                // Ambil semua data dari tb_daily_production
                const productionRef = ref(database, "db_productions/tb_daily_production");
                const snapshot = await get(productionRef);
                
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    let latestDate = null;
                    
                    // Loop melalui semua record untuk mencari last_imported_at terbaru
                    Object.values(data).forEach(record => {
                        if (record.last_imported_at) {
                            const recordDate = new Date(record.last_imported_at);
                            if (!latestDate || recordDate > latestDate) {
                                latestDate = recordDate;
                            }
                        }
                    });
                    
                    if (latestDate) {
                        console.log("✅ Last updated found:", latestDate);
                        setLastUpdated(latestDate);
                    } else {
                        console.log("⚠️ No last_imported_at found in any record");
                        setLastUpdated(null);
                    }
                } else {
                    console.log("⚠️ No data found in tb_daily_production");
                    setLastUpdated(null);
                }
            } catch (error) {
                console.error("❌ Error fetching last updated time:", error);
                setLastUpdated(null);
            }
        };

        fetchLastUpdated();
    }, []); // Empty dependency array berarti hanya dijalankan sekali saat komponen mount

    // Hitung total rencana dan aktual per bulan
    const calculateMonthlyGrandTotals = () => {
        const monthlyTotals = {
            rencana: {},
            aktual: {}
        };

        activeMonths.forEach(month => {
            monthlyTotals.rencana[month] = 0;
            monthlyTotals.aktual[month] = 0;
        });

        productionSummary.forEach(row => {
            activeMonths.forEach(month => {
                monthlyTotals.rencana[month] += row[`rencana_${month}`] || 0;
                monthlyTotals.aktual[month] += row[`aktual_${month}`] || 0;
            });
        });

        return monthlyTotals;
    };

    const monthlyGrandTotals = calculateMonthlyGrandTotals();

    // Siapkan data untuk chart
    const chartData = {
        months: activeMonths.map(month => {
            const monthNames = [
                'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
                'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'
            ];
            return monthNames[month - 1];
        }),
        rencana: activeMonths.map(month => monthlyGrandTotals.rencana[month] || 0),
        aktual: activeMonths.map(month => monthlyGrandTotals.aktual[month] || 0)
    };

    // Hitung nilai maksimum untuk skala chart
    const maxValue = Math.max(
        ...chartData.rencana,
        ...chartData.aktual,
        1 // Minimal 1 agar chart tidak kosong
    );

    // Tentukan interval berdasarkan nilai maksimum
    const getTickInterval = (max) => {
        if (max <= 10) return 2;
        if (max <= 50) return 10;
        if (max <= 100) return 20;
        if (max <= 500) return 100;
        if (max <= 1000) return 200;
        if (max <= 5000) return 1000;
        if (max <= 10000) return 2000;
        if (max <= 50000) return 10000;
        if (max <= 100000) return 20000;
        if (max <= 500000) return 100000;
        return 200000;
    };

    const tickInterval = getTickInterval(maxValue);

    // Buat array nilai tick dari 0 hingga maxValue dengan interval yang sesuai
    const tickValues = [];
    for (let i = 0; i <= maxValue; i += tickInterval) {
        tickValues.push(i);
    }
    // Pastikan nilai maksimum tercakup
    if (tickValues[tickValues.length - 1] < maxValue) {
        tickValues.push(maxValue);
    }

    // Hitung total keseluruhan
    const totalRencana = Object.values(monthlyGrandTotals.rencana).reduce((a, b) => a + b, 0);
    const totalAktual = Object.values(monthlyGrandTotals.aktual).reduce((a, b) => a + b, 0);
    const persentaseCapai = totalRencana > 0 ? ((totalAktual / totalRencana) * 100).toFixed(1) : 0;

    // Dapatkan rentang bulan untuk judul
    const getMonthRangeText = () => {
        if (activeMonths.length === 0) return `Tahun ${selectedYear}`;
        
        const monthNames = [
            'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
            'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
        ];
        
        const firstMonth = monthNames[activeMonths[0] - 1];
        const lastMonth = monthNames[activeMonths[activeMonths.length - 1] - 1];
        
        if (activeMonths.length === 1) {
            return `${firstMonth} ${selectedYear}`;
        } else {
            return `${firstMonth} - ${lastMonth} ${selectedYear}`;
        }
    };

    // Jika tidak ada data, tampilkan pesan kosong
    if (productionSummary.length === 0 || activeMonths.length === 0) {
        return (
            <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        Bar Chart Production Summary
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Tidak ada data untuk ditampilkan pada periode {getMonthRangeText()}
                    </Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 0, borderRadius: 0, boxShadow: 0 }}>
            {/* Header Chart */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" color="text.primary">
                        Grand Total Produksi Rencana vs Aktual
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Periode: {getMonthRangeText()}
                    </Typography>
                </Box>
                {/* Summary Cards */}
                <Box sx={{ 
                    display: 'flex', 
                    gap: 3, 
                    mt: 0,
                    flexWrap: 'wrap'
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <Box sx={{ 
                            width: 16, 
                            height: 16, 
                            bgcolor: theme.palette.primary.main,
                            borderRadius: 1
                        }} />
                        <Typography variant="body2">
                            Total Rencana: {formatThousandsNoDecimal(totalRencana)}
                        </Typography>
                    </Box>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <Box sx={{ 
                            width: 16, 
                            height: 16, 
                            bgcolor: theme.palette.success.main,
                            borderRadius: 1
                        }} />
                        <Typography variant="body2">
                            Total Aktual: {formatThousandsNoDecimal(totalAktual)}
                        </Typography>
                    </Box>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: theme.palette.info.light,
                        px: 2,
                        py: 0.5,
                        borderRadius: 2
                    }}>
                        <Typography variant="body2" fontWeight="bold">
                            Pencapaian: {persentaseCapai}%
                        </Typography>
                    </Box>
                </Box>
            </Stack>

            {/* Bar Chart */} {/* barLabel="" Menampilkan value di dalam bar batang*/}
            <Box sx={{ width: '100%', height: 400 }}>
                <BarChart
                    xAxis={[{
                        scaleType: 'band',
                        data: chartData.months,
                        tickLabelStyle: {
                            fontSize: 12,
                        },
                        label: 'Bulan',
                        labelStyle: {
                            fontSize: 14,
                            fontWeight: 'bold',
                        },
                    }]}
                    yAxis={[{
                        tickInterval: tickValues,
                        tickLabelStyle: {
                            fontSize: 12,
                        },
                        valueFormatter: (value) => formatThousandsNoDecimal(value),
                        label: '',
                        labelStyle: {
                            fontSize: 14,
                            fontWeight: 'bold',
                        },
                    }]}
                    series={[
                        {
                            data: chartData.rencana,
                            label: 'Rencana',
                            color: theme.palette.primary.main,
                            valueFormatter: (value) => formatThousandsNoDecimal(value),
                        },
                        {
                            data: chartData.aktual,
                            label: 'Aktual',
                            color: theme.palette.success.main,
                            valueFormatter: (value) => formatThousandsNoDecimal(value),
                        },
                    ]}
                    layout="vertical"
                    barLabel="" 
                    borderRadius={6}
                    slotProps={{
                        legend: {
                            direction: 'row',
                            position: { vertical: 'top', horizontal: 'right' },
                            padding: 0,
                            labelStyle: {
                                fontSize: 12,
                            },
                        },
                    }}
                    margin={{ top: 5, bottom: 0, left: 0, right: 0 }}
                    sx={{
                        [`.${axisClasses.left} .${axisClasses.label}`]: {
                            transform: 'translateX(-20px)',
                        },
                    }}
                />
            </Box>

            {/* Footer dengan informasi tambahan */}
            <Box sx={{ 
                mt: 3, 
                pt: 2, 
                borderTop: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2
            }}>
                <Typography variant="caption" color="text.secondary">
                    Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/*
                    <Typography variant="caption" color="text.secondary">
                        Total Kategori: {productionSummary.length}
                    </Typography>
                    */}
                    <Typography variant="caption" color="text.secondary">
                        Bulan Aktif: {activeMonths.length}
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
};

export default ProductionBarChart;